import type { Bar } from "@ortex-charts/math";

/**
 * The price bars an ORTEX datafeed has fetched or is fetching, kept apart from
 * the datafeed so that one store can serve every datafeed a chart creates.
 *
 * `StockChart` creates its datafeed again when the app's socket arrives, which
 * happens after load and can be before the chart has mounted. The page starts
 * the chart's first bar request before the chart exists (#1324); kept on the
 * datafeed, that request was lost with it and the chart asked a second time.
 */

export interface BarsAnswer {
    bars: Bar[];
    noMoreData: boolean;
}

interface Held extends BarsAnswer {
    at: number;
    from: number;
    to: number;
}

/** How long a fetched daily series is reused before it is asked for again. */
export const BAR_CACHE_MS = 5 * 60_000;
/** Expired windows used to be skipped but kept, a full history each; drop them, and never hold more than a few. */
const BAR_CACHE_MAX = 6;
const DAY_MS = 86_400_000;

/**
 * The window `@ortex-charts/financial` asks for on a symbol's first request
 * (`bindDatafeed` in dist/datafeed.js, 1.0.32): `initialBars` bars and 60 %
 * more back from now, and one bar ahead. A page that wants the chart's first
 * bars early asks for this window, so the chart's own request finds it.
 */
export function firstBarsWindow(now: number, barMs: number, initialBars: number): { from: number; to: number } {
    return { from: now - barMs * initialBars * 1.6, to: now + barMs };
}

/** A window fetched for [from, to] answers a request for [from', to'] when it reaches a day past either end (the platform answers by whole days). */
function covers(w: { from: number; to: number }, from: number, to: number): boolean {
    return w.from <= from + DAY_MS && w.to >= to - DAY_MS;
}

function slice(answer: BarsAnswer, from: number, to: number): BarsAnswer {
    return { bars: answer.bars.filter((b) => b.time >= from && b.time <= to), noMoreData: answer.noMoreData };
}

export interface BarStore {
    /**
     * A held series that already covers the window being asked for, sliced to
     * it. Held by span rather than by exact window so a wider fetch answers a
     * narrower question.
     */
    held(key: string, from: number, to: number): BarsAnswer | null;
    /**
     * The bars for `key` over [from, to]: from a request already in flight
     * whose window covers this one, or else from `fetch`, which is then the
     * request later callers share. Whatever `fetch` returns is held for
     * `BAR_CACHE_MS`. A failed request is forgotten, and a caller that was
     * waiting on it makes its own.
     */
    share(key: string, from: number, to: number, fetch: () => Promise<BarsAnswer>): Promise<BarsAnswer>;
    /** Where the REST series ended, per symbol and resolution, so the subscription knows whether today is still missing. */
    lastRestBar: Map<string, number>;
}

export function createBarStore(now: () => number = Date.now): BarStore {
    const barCache = new Map<string, Held>();
    const pending = new Map<string, { from: number; to: number; p: Promise<BarsAnswer> }>();

    function remember(key: string, entry: Held) {
        barCache.forEach((v, k) => entry.at - v.at > BAR_CACHE_MS && barCache.delete(k));
        barCache.delete(key);
        barCache.set(key, entry);
        if (barCache.size > BAR_CACHE_MAX) barCache.delete(barCache.keys().next().value as string);
    }

    return {
        held(key, from, to) {
            const hit = barCache.get(key);
            if (!hit || now() - hit.at > BAR_CACHE_MS) return null;
            if (!covers(hit, from, to)) return null;
            return slice(hit, from, to);
        },

        async share(key, from, to, fetch) {
            const inFlight = pending.get(key);
            if (inFlight && covers(inFlight, from, to)) {
                try {
                    return slice(await inFlight.p, from, to);
                } catch {
                    // The shared request failed; this caller asks for itself.
                }
            }
            const p = fetch();
            pending.set(key, { from, to, p });
            try {
                const answer = await p;
                remember(key, { at: now(), from, to, bars: answer.bars.slice(), noMoreData: answer.noMoreData });
                return answer;
            } finally {
                if (pending.get(key)?.p === p) pending.delete(key);
            }
        },

        lastRestBar: new Map<string, number>(),
    };
}
